import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import flagsmith from 'flagsmith'
import { FlagsmithProvider } from 'flagsmith/react'
import { getFlagsmithAPIKey } from './utils/constants'

import createStore from 'store/createStore'

import './styles/main.scss'
// Self-host Font Awesome (includes webfonts); avoids CDN load failures that garble icons.
import '@fortawesome/fontawesome-free/css/all.min.css'
import '@fortawesome/fontawesome-free/css/v4-shims.min.css'
import './utils/object'
import { loadRuntimeConfig } from './utils/loadRuntimeConfig'
import { initSentry } from './utils/sentry'
import { fetchUser } from 'store/user'
import AppRouter from './router/AppRouter'

// ========================================================
// Store Instantiation
// ========================================================

const store = createStore(
  window.___INITIAL_STATE__,
  window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__(),
)

// QA testing: initialize Sentry in non-prod environments as well.
if (__PROD__) {
  initSentry()
}

// ========================================================
// Render Setup (React 18 createRoot API)
// ========================================================
const MOUNT_NODE = document.getElementById('root')
let root = null

const renderApp = () => {
  const FLAGSMITH_API_KEY = getFlagsmithAPIKey()

  const app = (
    <FlagsmithProvider
      options={{
        environmentID: FLAGSMITH_API_KEY,
      }}
      flagsmith={flagsmith}>
      <Provider store={store}>
        <div style={{ height: '100%' }}>
          <AppRouter store={store} onUpdate={() => window.scrollTo(0, 0)} />
        </div>
      </Provider>
    </FlagsmithProvider>
  )

  if (!root) root = createRoot(MOUNT_NODE)
  root.render(app)
}

// ========================================================
// Bootstrap (load config + hydrate user before rendering)
// ========================================================
const bootstrap = async () => {
  try {
    // 1. Load config before rendering
    await loadRuntimeConfig()
    // 2. Try to fetch user from backend (cookie)
    await store.dispatch(fetchUser({}, true, false, false, true))
    // 3. Render app
    renderApp()
  } catch (err) {
    console.error('Bootstrap error:', err)
    renderApp() // still render even if fetchUser fails
  }
}

// This code is excluded from production bundle
if (__DEV__ && module.hot) {
  const renderError = error => {
    const RedBox = require('redbox-react').default
    if (!root) root = createRoot(MOUNT_NODE)
    root.render(<RedBox error={error} />)
  }

  const safeRender = () => {
    try {
      bootstrap()
    } catch (error) {
      console.error(error)
      renderError(error)
    }
  }

  module.hot.accept('./router/AppRoutes', () => {
    // Defer re-render until after HMR apply (setImmediate is Node-only)
    setTimeout(() => {
      if (root) {
        root.unmount()
        root = null
      }
      safeRender()
    }, 0)
  })

  safeRender()
} else {
  bootstrap()
}
